import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { Box, Skeleton } from '../../../../1-primative';
import {
  Table, TableRow, TableBody, TableCell,
  Pagination,
  TableHeadCell,
} from '../../../../2-component';
import { ViewFormDialog } from '../../../workflowCompletion/subSteps/signFormCompletions/viewFormDialog';
import { translateBackend } from '../../../../../assets/i18n/config';
import { useLocalization } from '../../../../../util/useLocalization';

const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        state
        translatedName { en fr }
        workflowCompletion {
          id
          workflow {
            name { en fr }
          }
        }
        signatures {
          organizationUser {
            id
            firstName
            lastName
          }
          signedAt
        }
      }
      totalCount
    }
  }
`;

export const FormCompletions = ({
  userId,
}: {
  userId: string;
}) => {
  const [activeFormCompletion, setActiveFormCompletion] = useState<any>(null);
  const { t } = useTranslation('document');
  const [page, setPage] = useState(1);
  const [file, setFile] = useState<File>();
  const { localizedDateTime } = useLocalization();
  const PER_PAGE = 15;

  const { data, previousData, loading } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          userId,
          states: ['SIGNED'],
        },
        pagination: { perPage: PER_PAGE, offSet: (page - 1) * PER_PAGE },
      },
    },
  });

  return (
    <Box m={-2}>
      <Table>
        <TableBody>
          <TableRow>
            <TableHeadCell>{t('formCompletions.table.name')}</TableHeadCell>
            <TableHeadCell>{t('formCompletions.table.package')}</TableHeadCell>
            <TableHeadCell>{t('formCompletions.table.signedBy')}</TableHeadCell>
            <TableHeadCell>{t('formCompletions.table.signedAt')}</TableHeadCell>
          </TableRow>
          { loading && !previousData && [...Array(PER_PAGE)].map((x: any, i: number) => (
            <TableRow key={i}>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
            </TableRow>
          ))}
          {(data || previousData)?.fetchFormCompletions?.formCompletions?.map((formCompletion: any) => (
            <TableRow
              key={formCompletion.id}
              onClick={() => setActiveFormCompletion(formCompletion)}
              pointer hover
            >
              <TableCell>{translateBackend(formCompletion.translatedName)}</TableCell>
              <TableCell>{translateBackend(formCompletion.workflowCompletion.workflow.name)}</TableCell>
              <TableCell>
                {
                  formCompletion.signatures.length > 0
                    ? `${formCompletion.signatures[0]?.organizationUser?.firstName} ${formCompletion.signatures[0]?.organizationUser?.lastName}`
                    : t('signedExternally')
                }
              </TableCell>
              <TableCell>{localizedDateTime(formCompletion.signatures[0]?.signedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box mt={1} display='flex' justifyContent='end'>
        <Pagination
          total={data?.fetchFormCompletions?.totalCount}
          count={Math.ceil((data?.fetchFormCompletions?.totalCount || 0) / PER_PAGE)}
          page={page}
          perPage={PER_PAGE}
          onChange={(_e, newPage) => {
            setPage(newPage);
          }}
        />
      </Box>
      { activeFormCompletion && (
        <ViewFormDialog formCompletionId={activeFormCompletion.id} onClose={() => setActiveFormCompletion(null)} file={file} setFile={setFile} signLoading={false} />
      )}
    </Box>
  );
};
